const RIGHT_INSPECTION_ROW_COUNT = 31;
const LEFT_INSPECTION_ROW_COUNT = 31;
const LEFT_INSPECTION_START_NO = 32;

const createBlankChecklistRows = (count, startNo) =>
  Array.from({ length: count }, (_, index) => ({
    no: startNo + index,
    en: "",
    ar: "",
    checked: false,
    notes: "",
    groupAr: "",
    groupEn: "",
    groupRowspan: 0,
    isGroupStart: false,
  }));

const normalizeInspectionChecklistItem = (item = {}) => {
  const checked =
    typeof item?.present === "boolean" ? Boolean(item.present) : Boolean(item?.checked);

  return {
    en: item?.name || item?.en || "",
    ar: item?.arabicName || item?.ar || "",
    checked,
    notes: typeof item?.notes === "string" ? item.notes : "",
  };
};

const normalizeInspectionChecklistGroups = (checklist = []) =>
  checklist
    .map((group) => {
      const sourceItems = Array.isArray(group?.items) ? group.items : [group];
      const items = sourceItems
        .map((item) => normalizeInspectionChecklistItem(item))
        .filter((item) => item.en || item.ar);

      return {
        groupEn: group?.name || group?.groupEn || "",
        groupAr: group?.arabicName || group?.groupAr || "",
        items,
      };
    })
    .filter((group) => group.items.length);

const pushGroupIntoColumn = (columnGroups, group, takeCount) => {
  if (!takeCount) {
    return;
  }

  columnGroups.push({
    groupEn: group.groupEn,
    groupAr: group.groupAr,
    items: group.items.slice(0, takeCount),
  });

  group.items = group.items.slice(takeCount);
};

const splitChecklistGroupsAcrossColumns = (groups) => {
  const rightGroups = [];
  const leftGroups = [];
  let rightUsed = 0;
  let leftUsed = 0;

  groups.forEach((originalGroup) => {
    const group = {
      groupEn: originalGroup.groupEn,
      groupAr: originalGroup.groupAr,
      items: [...originalGroup.items],
    };

    if (!group.items.length) {
      return;
    }

    if (rightUsed + group.items.length <= RIGHT_INSPECTION_ROW_COUNT) {
      pushGroupIntoColumn(rightGroups, group, group.items.length);
      rightUsed += rightGroups[rightGroups.length - 1].items.length;
      return;
    }

    if (rightUsed < RIGHT_INSPECTION_ROW_COUNT) {
      const remainingRight = RIGHT_INSPECTION_ROW_COUNT - rightUsed;
      pushGroupIntoColumn(rightGroups, group, remainingRight);
      rightUsed = RIGHT_INSPECTION_ROW_COUNT;
    }

    if (group.items.length && leftUsed < LEFT_INSPECTION_ROW_COUNT) {
      const remainingLeft = LEFT_INSPECTION_ROW_COUNT - leftUsed;
      const takeLeft = Math.min(remainingLeft, group.items.length);
      pushGroupIntoColumn(leftGroups, group, takeLeft);
      leftUsed += takeLeft;
    }
  });

  return { rightGroups, leftGroups };
};

const buildRowsFromGroups = (groups, count, startNo) => {
  const rows = [];
  let nextNo = startNo;

  groups.forEach((group) => {
    group.items.forEach((item, index) => {
      rows.push({
        ...item,
        no: nextNo++,
        groupAr: group.groupAr,
        groupEn: group.groupEn,
        groupRowspan: index === 0 ? group.items.length : 0,
        isGroupStart: index === 0 && Boolean(group.groupAr || group.groupEn),
      });
    });
  });

  while (rows.length < count) {
    rows.push({
      no: startNo + rows.length,
      en: "",
      ar: "",
      checked: false,
      notes: "",
      groupAr: "",
      groupEn: "",
      groupRowspan: 0,
      isGroupStart: false,
    });
  }

  return rows;
};

export const DEFAULT_RIGHT_INSPECTION_CHECKLIST = createBlankChecklistRows(
  RIGHT_INSPECTION_ROW_COUNT,
  1
);

export const DEFAULT_LEFT_INSPECTION_CHECKLIST = createBlankChecklistRows(
  LEFT_INSPECTION_ROW_COUNT,
  LEFT_INSPECTION_START_NO
);

export const buildInspectionChecklistFromApi = (checklist = []) => {
  const normalizedGroups = normalizeInspectionChecklistGroups(checklist);
  const { rightGroups, leftGroups } = splitChecklistGroupsAcrossColumns(normalizedGroups);

  return {
    rightItems: buildRowsFromGroups(rightGroups, RIGHT_INSPECTION_ROW_COUNT, 1),
    leftItems: buildRowsFromGroups(
      leftGroups,
      LEFT_INSPECTION_ROW_COUNT,
      LEFT_INSPECTION_START_NO
    ),
  };
};
